import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../../../lib/api";

export const BALANCE_EVENT = "emergent-balance-changed";

const message = (s) => {
  const left = Math.max(0, s.est_left).toLocaleString("en-IN");
  if (s.paused) return `Customer builds are paused — your Emergent credits have run out. Recharge, then record it here to resume.`;
  if (s.empty) return `Your Emergent credits have run out. Customers can still build — recharge now so builds don't fail.`;
  return `Your Emergent credits are running low — about ${left} left (alert below ${s.low_threshold.toLocaleString("en-IN")}). Recharge soon.`;
};

export const RechargeBanner = () => {
  const [s, setS] = useState(null);
  const { pathname } = useLocation();
  const load = useCallback(() => api.adminCreditsStatus().then(setS).catch(() => {}), []);
  useEffect(() => { load(); }, [load, pathname]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    window.addEventListener(BALANCE_EVENT, load);
    return () => { clearInterval(t); window.removeEventListener(BALANCE_EVENT, load); };
  }, [load]);
  if (!s) return null;
  return (
    <>
      {s.panel_low && <PanelAlert s={s} />}
      {(s.low || s.empty) && <EmergentAlert s={s} />}
    </>
  );
};

const PanelAlert = ({ s }) => (
  <div role="alert" data-testid="panel-low-banner" className="mb-3 rounded-2xl bg-rose-600 text-white px-4 py-3 flex flex-wrap items-center gap-3 shadow-sm">
    <AlertTriangle size={18} className="shrink-0" />
    <p className="flex-1 min-w-[220px] text-sm font-medium" data-testid="panel-low-banner-message">
      {s.panel_balance <= 0 ? `Your panel balance is ${s.panel_balance.toLocaleString("en-IN")} credits. Customer recharges are taken from it — top up your panel.`
        : `Your panel balance is low — ${s.panel_balance.toLocaleString("en-IN")} credits left. Top up soon.`}
    </p>
    <Link to="/admin/credits?panel=1" data-testid="panel-low-banner-topup"
      className="h-8 px-3 rounded-full bg-white text-rose-700 hover:bg-rose-50 text-xs font-semibold flex items-center transition-colors">Top up panel</Link>
  </div>
);

const EmergentAlert = ({ s }) => (
  <div role="alert" data-testid="recharge-banner" className="mb-6 rounded-2xl bg-rose-600 text-white px-4 py-3 flex flex-wrap items-center gap-3 shadow-sm">
    <AlertTriangle size={18} className="shrink-0" />
    <p className="flex-1 min-w-[220px] text-sm font-medium" data-testid="recharge-banner-message">{message(s)}</p>
    <a href="https://app.emergent.sh" target="_blank" rel="noreferrer" data-testid="recharge-banner-emergent"
      className="h-8 px-3 rounded-full bg-white/15 hover:bg-white/25 text-xs font-semibold flex items-center gap-1.5 transition-colors">Recharge on Emergent <ExternalLink size={12} /></a>
    <Link to="/admin/credits?topup=1" data-testid="recharge-banner-record"
      className="h-8 px-3 rounded-full bg-white text-rose-700 hover:bg-rose-50 text-xs font-semibold flex items-center transition-colors">Record recharge</Link>
  </div>
);

export default RechargeBanner;
